import React, { useState } from "react";
import {
  Card,
  Form,
  Select,
  DatePicker,
  Button,
  Table,
  Space,
  Radio,
  Row,
  Col,
  Spin,
  Statistic,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import axios from "axios";
import {
  getProductionReport,
  getJobCompletionReport,
  getWastageReport,
  getMaterialConsumptionReport,
  getCustomerSalesReport,
  getOrderPipelineReport,
} from "../../../api/dashboardApi";
import PageTitle from "../../page-header/PageHeader";
import checkTokenExp from "../../../utils/checkTokenExp";

const { RangePicker } = DatePicker;

const reportTypes = [
  { value: "production", label: "Production Report" },
  { value: "job-completion", label: "Job Completion Report" },
  { value: "wastage", label: "Wastage Report" },
  { value: "material-consumption", label: "Material Consumption" },
  { value: "customer-sales", label: "Customer Sales" },
  { value: "order-pipeline", label: "Order Pipeline" },
];

const ReportGenerator = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [form] = Form.useForm();
  const [reportType, setReportType] = useState("production");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Generate report ────────────────────────────────────────
  const handleGenerate = async () => {
    const values = form.getFieldsValue();
    setLoading(true);
    setReportData(null);

    try {
      const params = {};
      if (values.dateRange?.[0]) {
        params.from_date = values.dateRange[0].format("YYYY-MM-DD");
      }
      if (values.dateRange?.[1]) {
        params.to_date = values.dateRange[1].format("YYYY-MM-DD");
      }
      if (values.category) {
        params.category = values.category;
      }

      let data;
      switch (reportType) {
        case "production":
          data = await getProductionReport(params);
          break;
        case "job-completion":
          data = await getJobCompletionReport(params);
          break;
        case "wastage":
          data = await getWastageReport(params);
          break;
        case "material-consumption":
          data = await getMaterialConsumptionReport(params);
          break;
        case "customer-sales":
          data = await getCustomerSalesReport(params);
          break;
        case "order-pipeline":
          data = await getOrderPipelineReport();
          break;
        default:
          toast.error("Unknown report type");
          return;
      }

      setReportData(data);
      toast.success("Report generated");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate report"
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Download (if backend supports) ─────────────────────────
  const handleDownload = async (format) => {
    const values = form.getFieldsValue();
    setDownloading(true);

    try {
      const params = { format };
      if (values.dateRange?.[0]) {
        params.from_date = values.dateRange[0].format("YYYY-MM-DD");
      }
      if (values.dateRange?.[1]) {
        params.to_date = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(`reports/${reportType}/download`, {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${reportType}_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Download not available for this report type");
    } finally {
      setDownloading(false);
    }
  };

  // ══════════════════════════════════════════════════════════
  // Dynamic columns based on report type + actual API response
  // ══════════════════════════════════════════════════════════

  const getColumnsAndData = () => {
    if (!reportData) return { columns: [], dataSource: [] };

    switch (reportType) {
      case "production": {
        const cols = [
          { title: "Job #", dataIndex: "job_number", key: "job_number" },
          { title: "Item", dataIndex: "item_name", key: "item_name", ellipsis: true },
          {
            title: "Completed Qty",
            dataIndex: "completed_quantity",
            key: "completed_quantity",
            align: "right",
            render: (v) => (v || 0).toLocaleString(),
          },
          {
            title: "Rejected Qty",
            dataIndex: "rejected_quantity",
            key: "rejected_quantity",
            align: "right",
            render: (v) => (v > 0 ? <span style={{ color: "#f5222d" }}>{v.toLocaleString()}</span> : "0"),
          },
        ];
        return { columns: cols, dataSource: reportData.completed_jobs || [] };
      }

      case "job-completion": {
        const cols = [
          { title: "Job #", dataIndex: "job_number", key: "job_number" },
          { title: "Item", dataIndex: "item_name", key: "item_name", ellipsis: true },
          {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            render: (v) => {
              const color = v === "urgent" ? "red" : v === "high" ? "orange" : "default";
              return <span style={{ textTransform: "capitalize", color: color === "default" ? undefined : color }}>{v}</span>;
            },
          },
          {
            title: "Expected Delivery",
            dataIndex: "expected_delivery",
            key: "expected_delivery",
            render: (v) => (v ? moment(v).format("DD-MMM-YYYY") : "—"),
          },
          {
            title: "Completed At",
            dataIndex: "completed_at",
            key: "completed_at",
            render: (v) => (v ? moment(v).format("DD-MMM-YYYY") : "—"),
          },
          {
            title: "On Time",
            dataIndex: "on_time",
            key: "on_time",
            render: (v) =>
              v ? (
                <span style={{ color: "#52c41a" }}>✓ Yes</span>
              ) : (
                <span style={{ color: "#f5222d" }}>✗ No</span>
              ),
          },
          {
            title: "Ordered",
            dataIndex: "ordered_qty",
            key: "ordered_qty",
            align: "right",
            render: (v) => (v || 0).toLocaleString(),
          },
          {
            title: "Completed",
            dataIndex: "completed_qty",
            key: "completed_qty",
            align: "right",
            render: (v) => (v || 0).toLocaleString(),
          },
        ];
        return { columns: cols, dataSource: reportData.jobs || [] };
      }

      case "wastage": {
        const byJob = reportData.by_job || [];
        const cols = [
          { title: "Job #", dataIndex: "job_number", key: "job_number" },
          {
            title: "Total Produced",
            dataIndex: "total_produced",
            key: "total_produced",
            align: "right",
            render: (v) => (v || 0).toLocaleString(),
          },
          {
            title: "Total Rejected",
            dataIndex: "total_rejected",
            key: "total_rejected",
            align: "right",
            render: (v) => (v > 0 ? <span style={{ color: "#f5222d" }}>{v.toLocaleString()}</span> : "0"),
          },
          {
            title: "Wastage %",
            dataIndex: "wastage_percent",
            key: "wastage_percent",
            align: "right",
            render: (v) => {
              const pct = (v || 0).toFixed(2);
              return (
                <span style={{ color: v > 3 ? "#f5222d" : v > 1.5 ? "#faad14" : "#52c41a" }}>
                  {pct}%
                </span>
              );
            },
          },
        ];
        return { columns: cols, dataSource: byJob };
      }

      case "material-consumption": {
        const cols = [
          { title: "Material Code", dataIndex: "material_code", key: "material_code" },
          { title: "Material", dataIndex: "material_name", key: "material_name", ellipsis: true },
          { title: "Category", dataIndex: "category", key: "category" },
          {
            title: "Total Consumed",
            dataIndex: "total_consumed",
            key: "total_consumed",
            align: "right",
            render: (v) => (v || 0).toLocaleString(),
          },
          { title: "Unit", dataIndex: "unit", key: "unit" },
          {
            title: "Jobs Used In",
            dataIndex: "jobs_used_in",
            key: "jobs_used_in",
            align: "right",
          },
        ];
        return { columns: cols, dataSource: reportData.consumption || [] };
      }

      case "customer-sales": {
        const cols = [
          { title: "Customer ID", dataIndex: "customer_id", key: "customer_id" },
          {
            title: "Orders",
            dataIndex: "order_count",
            key: "order_count",
            align: "right",
          },
          {
            title: "Total Value (₹)",
            dataIndex: "total_value",
            key: "total_value",
            align: "right",
            render: (v) => `₹${(v || 0).toLocaleString()}`,
            sorter: (a, b) => (a.total_value || 0) - (b.total_value || 0),
            defaultSortOrder: "descend",
          },
          {
            title: "Dispatched (₹)",
            dataIndex: "dispatched_value",
            key: "dispatched_value",
            align: "right",
            render: (v) => `₹${(v || 0).toLocaleString()}`,
          },
        ];
        return { columns: cols, dataSource: reportData.customers || [] };
      }

      case "order-pipeline": {
        const cols = [
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (v) => (v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          },
          {
            title: "Count",
            dataIndex: "count",
            key: "count",
            align: "right",
          },
          {
            title: "Value (₹)",
            dataIndex: "value",
            key: "value",
            align: "right",
            render: (v) => `₹${(v || 0).toLocaleString()}`,
          },
        ];
        return {
          columns: cols,
          dataSource: reportData.orders_by_status || [],
        };
      }

      default:
        return { columns: [], dataSource: [] };
    }
  };

  // ── Summary cards from report data ─────────────────────────
  const renderSummary = () => {
    if (!reportData) return null;
    let items = [];

    switch (reportType) {
      case "production":
        if (reportData.summary) {
          const s = reportData.summary;
          items = [
            { label: "Jobs Completed", value: s.total_jobs_completed },
            { label: "Quantity Produced", value: s.total_quantity_produced },
            { label: "Rejected", value: s.total_rejected },
            { label: "Excess", value: s.total_excess },
          ];
        }
        break;
      case "job-completion":
        items = [
          { label: "Total Completed", value: reportData.total_completed },
          { label: "On Time", value: reportData.on_time },
          { label: "Delayed", value: reportData.delayed },
          { label: "On-Time %", value: `${reportData.on_time_percentage || 0}%` },
          { label: "Avg Lead Time", value: `${reportData.average_lead_time_days || 0} days` },
        ];
        break;
      case "wastage":
        if (reportData.summary) {
          const s = reportData.summary;
          items = [
            { label: "Total Produced", value: s.total_produced },
            { label: "Total Rejected", value: s.total_rejected },
            { label: "Rejection %", value: `${(s.overall_rejection_percent || 0).toFixed(2)}%` },
          ];
        }
        break;
      case "customer-sales":
        items = [
          { label: "Total Customers", value: reportData.total_customers },
          { label: "Total Orders", value: reportData.total_orders },
          { label: "Total Value", value: `₹${(reportData.total_value || 0).toLocaleString()}` },
        ];
        break;
      case "order-pipeline":
        if (reportData.quotation_metrics) {
          const q = reportData.quotation_metrics;
          items = [
            { label: "Quotations (90d)", value: q.total_quotations_90_days },
            { label: "Converted", value: q.converted },
            { label: "Conversion Rate", value: `${q.conversion_rate || 0}%` },
          ];
        }
        break;
      default:
        break;
    }

    if (items.length === 0) return null;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {items.map((item, i) => (
          <Col key={i} xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title={item.label}
                value={
                  typeof item.value === "number"
                    ? item.value.toLocaleString()
                    : item.value
                }
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const { columns, dataSource } = getColumnsAndData();

  return (
    <>
      <PageTitle title="Back" subtitle="Report Generator" />

      <Card title="Report Generator">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            dateRange: [moment().subtract(1, "month"), moment()],
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Report Type">
                <Select
                  value={reportType}
                  onChange={(val) => {
                    setReportType(val);
                    setReportData(null);
                  }}
                  options={reportTypes}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item name="dateRange" label="Date Range">
                <RangePicker
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {reportType === "material-consumption" && (
              <Col xs={24} sm={12} md={4}>
                <Form.Item name="category" label="Category">
                  <Select
                    allowClear
                    placeholder="All"
                    options={[
                      { value: "PAPER", label: "Paper" },
                      { value: "GUM_CORR", label: "Gum (Corr)" },
                      { value: "GUM_PASTE", label: "Gum (Paste)" },
                      { value: "INK", label: "Ink" },
                      { value: "OTHER", label: "Other" },
                    ]}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} sm={24} md={6}>
              <Form.Item label=" ">
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleGenerate}
                    loading={loading}
                  >
                    Generate
                  </Button>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={() => handleDownload("xlsx")}
                    loading={downloading}
                    disabled={!reportData}
                  >
                    Excel
                  </Button>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => handleDownload("pdf")}
                    loading={downloading}
                    disabled={!reportData}
                  >
                    PDF
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        )}

        {reportData && (
          <>
            {/* Summary Cards */}
            {renderSummary()}

            {/* Data Table */}
            <Table
              columns={columns}
              dataSource={dataSource}
              rowKey={(r, i) => r.job_number || r.customer_id || r.material_code || r.status || i}
              pagination={{ pageSize: 20 }}
              scroll={{ x: true }}
              size="small"
            />

            {/* Wastage: additional by_stage breakdown */}
            {reportType === "wastage" && reportData.by_stage && (
              <Card
                size="small"
                title="Wastage by Stage"
                style={{ marginTop: 16 }}
              >
                <Table
                  columns={[
                    { title: "Stage", dataIndex: "stage", key: "stage" },
                    {
                      title: "Total Output",
                      dataIndex: "total_output",
                      key: "total_output",
                      align: "right",
                      render: (v) => (v || 0).toLocaleString(),
                    },
                    {
                      title: "Total Wastage",
                      dataIndex: "total_wastage",
                      key: "total_wastage",
                      align: "right",
                      render: (v) => (v || 0).toLocaleString(),
                    },
                    {
                      title: "Wastage %",
                      dataIndex: "wastage_percent",
                      key: "wastage_percent",
                      align: "right",
                      render: (v) => `${(v || 0).toFixed(2)}%`,
                    },
                  ]}
                  dataSource={Object.values(reportData.by_stage)}
                  rowKey={(r) => r.stage}
                  pagination={false}
                  size="small"
                />
              </Card>
            )}

            {/* Order pipeline: upcoming deliveries */}
            {reportType === "order-pipeline" &&
              reportData.upcoming_deliveries?.length > 0 && (
                <Card
                  size="small"
                  title="Upcoming Deliveries"
                  style={{ marginTop: 16 }}
                >
                  <Table
                    columns={[
                      {
                        title: "Order",
                        dataIndex: "order_number",
                        key: "order_number",
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (v) =>
                          (v || "")
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase()),
                      },
                      {
                        title: "Expected Delivery",
                        dataIndex: "expected_delivery",
                        key: "expected_delivery",
                        render: (v) =>
                          v ? moment(v).format("DD-MMM-YYYY") : "—",
                      },
                      {
                        title: "Amount (₹)",
                        dataIndex: "total_amount",
                        key: "total_amount",
                        align: "right",
                        render: (v) => `₹${(v || 0).toLocaleString()}`,
                      },
                    ]}
                    dataSource={reportData.upcoming_deliveries}
                    rowKey={(r) => r.order_number}
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}
          </>
        )}
      </Card>
    </>
  );
};

export default ReportGenerator;
